import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';

export interface ConnectAccountStatus {
  exists: boolean;
  account_id?: string;
  onboarding_completed: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements?: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
    pending_verification: string[];
  };
  capabilities?: Record<string, string>;
  country?: string;
  default_currency?: string;
  business_profile?: {
    name?: string;
    url?: string;
    support_email?: string;
    support_phone?: string;
  };
}

export interface ExpressAccountHook {
  accountStatus: ConnectAccountStatus | null;
  loading: boolean;
  creating: boolean;
  error: string | null;
  createAccount: (businessId: string, countryCode?: string) => Promise<void>;
  refreshStatus: (businessId: string) => Promise<void>;
  generateOnboardingLink: (businessId: string) => Promise<string | null>;
  isReady: boolean;
  requiresAction: boolean;
}

export const useExpressAccount = (businessId: string): ExpressAccountHook => {
  const [accountStatus, setAccountStatus] = useState<ConnectAccountStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derived states
  const isReady = accountStatus?.onboarding_completed && accountStatus?.charges_enabled && accountStatus?.payouts_enabled;
  const requiresAction = accountStatus ? (
    (accountStatus.requirements?.past_due?.length || 0) > 0 ||
    (accountStatus.requirements?.currently_due?.length || 0) > 0
  ) : false;

  const checkAccountStatus = useCallback(async (businessId: string) => {
    if (!businessId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('express-account-management/account-status', {
        body: JSON.stringify({ business_id: businessId })
      });

      if (error) {
        throw new Error(error.message || 'Failed to check account status');
      }

      setAccountStatus(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to check account status';
      setError(errorMessage);
      console.error('Error checking account status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createAccount = useCallback(async (businessId: string, countryCode: string = 'SK') => {
    setCreating(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('express-account-management/create', {
        body: JSON.stringify({ 
          business_id: businessId,
          country_code: countryCode
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to create Express account');
      }

      toast.success("Express Account Created", { description: "Successfully created Stripe Connect account." });

      // Refresh status after creation
      await checkAccountStatus(businessId);
      
      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create Express account';
      setError(errorMessage);
      toast.error("Account Creation Failed", { description: errorMessage });
      throw err;
    } finally {
      setCreating(false);
    }
  }, [checkAccountStatus]);

  const refreshStatus = useCallback(async (businessId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('express-account-management/update-account', {
        body: JSON.stringify({ business_id: businessId })
      });

      if (error) {
        throw new Error(error.message || 'Failed to refresh account status');
      }

      setAccountStatus(data);
      
      if (data.onboarding_completed && !accountStatus?.onboarding_completed) {
        toast.success("Account Ready!", { description: "Your Stripe Connect account setup is complete." });
      }
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to refresh account status';
      setError(errorMessage);
      console.error('Error refreshing account status:', err);
    } finally {
      setLoading(false);
    }
  }, [accountStatus]);

  const generateOnboardingLink = useCallback(async (businessId: string): Promise<string | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('express-account-management/create-account-link', {
        body: JSON.stringify({ business_id: businessId })
      });

      if (error) {
        throw new Error(error.message || 'Failed to generate onboarding link');
      }

      toast.success("Onboarding Link Generated", { description: "Your Stripe Connect onboarding link is ready." });

      return data.url;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate onboarding link';
      setError(errorMessage);
      toast.error("Link Generation Failed", { description: errorMessage });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load account status on mount and when businessId changes
  useEffect(() => {
    if (businessId) {
      checkAccountStatus(businessId);
    }
  }, [businessId, checkAccountStatus]);

  // Set up real-time subscription for account status changes
  useEffect(() => {
    if (!businessId) return;

    const subscription = supabase
      .channel(`stripe_connect_accounts_${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stripe_connect_accounts',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;

          console.log('Express account updated:', payload);
          // Refresh status when account changes
          checkAccountStatus(businessId);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [businessId, checkAccountStatus]);

  return {
    accountStatus,
    loading,
    creating,
    error,
    createAccount,
    refreshStatus,
    generateOnboardingLink,
    isReady,
    requiresAction,
  };
};